import { useEffect, useState } from "react";
import { IMediaItem, IMediaTreeItem, IMediaVariantMetadata } from "../../../models/Interfaces";
import { Col, FormControl, FormLabel, InputGroup, Row } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faTimes } from "@fortawesome/free-solid-svg-icons";
import TreeSelect from "./TreeSelect";
import { useSiteMedia } from "../../../hooks/useSiteMedia";
import { Link } from "react-router-dom";
import { TreeItemSort } from "../../../config/Enums";
import AppEmptyState from "./AppEmptyState";

interface ISiteMediaPicker {
  existingUrl?: string;
  onMediaSelect: (selectedurl: string) => void;
  onMediaClear: () => void;
  captionLabel?: string;
};

const SiteMediaPicker = (props: ISiteMediaPicker) => {
  const media = useSiteMedia();
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const videoMimeTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/mov', 'video/avi', 'video/mpeg'];
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mpeg'];


  const getVariantLabel = (item: IMediaItem): string => {
    if (!item.MetadataJson) {
      return item.Filename;
    }

    try {
      const metadata = JSON.parse(item.MetadataJson) as IMediaVariantMetadata;

      if (metadata?.label) {
        return `${item.Filename} (${metadata.label})`;
      }

      if (metadata?.width && metadata?.height) {
        return `${item.Filename} (${metadata.width}x${metadata.height})`;
      }

      if (metadata?.width) {
        return `${item.Filename} (${metadata.width}w)`;
      }
    } catch {
      return item.Filename;
    }

    return item.Filename;
  };

  function normalizeMediaTree(items: Array<IMediaTreeItem | IMediaItem>): Array<IMediaTreeItem> {
    if (items.length === 0) {
      return [];
    }

    const first = items[0] as Partial<IMediaTreeItem> & Partial<IMediaItem>;

    if ("text" in first && "parent" in first && "itemid" in first) {
      return items as Array<IMediaTreeItem>;
    }

    const mediaItems = items as Array<IMediaItem>;
    const treeItems: Array<IMediaTreeItem> = [];

    mediaItems
      .filter((item) => !item.SourceFileId)
      .forEach((item) => {
        treeItems.push({
          id: item.Id.toString(),
          itemid: item.Id,
          parent: "#",
          parentid: 0,
          text: item.Filename,
          type: "file",
          url: item.Url,
          sourceFileId: null,
          metadataJson: item.MetadataJson ?? null,
          isGeneratedVariant: false
        });

        const variants = item.SrcSet ?? mediaItems.filter((child) => child.SourceFileId === item.Id);
        variants.forEach((variant) => {
          treeItems.push({
            id: `${item.Id}-${variant.Id}`,
            itemid: variant.Id,
            parent: item.Id.toString(),
            parentid: item.Id,
            text: getVariantLabel(variant),
            type: "file",
            url: variant.Url,
            sourceFileId: variant.SourceFileId ?? item.Id,
            metadataJson: variant.MetadataJson ?? null,
            isGeneratedVariant: true
          });
        });
      });

    return treeItems;
  };

  const siteMedia = normalizeMediaTree(media.items ?? []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedUrl(event.target.value);
    props.onMediaSelect(event.target.value);
  };

  const handleTreeSelect = (value: string): void => {
    if (value) {
      props.onMediaSelect(value);
    }
  };

  const mediaIsVideo = (): boolean => {
    if (props.existingUrl) {
      const fileType = props.existingUrl?.toLowerCase();

      if (videoMimeTypes.includes(fileType)) {
        return true;
      }

      // Fallback to check file extension if MIME type is not available
      const extension = props.existingUrl.split('.').pop()?.toLowerCase();
      
      if (extension && videoExtensions.includes(extension)) {
        return true;
      }
    }
    return false;
  };

  const clearImage = (): void => {
    setSelectedUrl("");
    if (props.onMediaClear) {
      props.onMediaClear();
    }
  };

  useEffect(() => {
    setSelectedUrl(props.existingUrl ?? "");
  }, [props.existingUrl]);

  return (
    <>
        <Row className="m-0 p-0">
          <Col md={12} xl={6} className="m-0 p-0">
            <FormLabel>{props.captionLabel ?? "Select image or URL to image location"}</FormLabel>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="enter an image url or select from uploaded media..."
                value={selectedUrl ?? ""}
                onChange={handleInputChange}>
              </FormControl>
              <InputGroupText >
                <div title="clear image" onClick={clearImage} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </InputGroupText>
            </InputGroup>
            <div className="d-flex align-items-center gap-2 mt-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={media.refresh} disabled={media.refreshing} aria-label="Refresh site images">
                <FontAwesomeIcon icon={faRefresh} spin={media.refreshing} /> Refresh
              </button>
              {media.refreshing && <span className="small text-muted" role="status">{media.items === undefined ? "Loading media…" : "Updating media…"}</span>}
            </div>
            {media.error && <div className="small text-warning mt-2" role="status">{media.error}</div>}
            {siteMedia.length > 0 ?
              (
                <InputGroup className="mt-3 w-100 flex-nowrap">
                  <TreeSelect
                    idKey="id"
                    parentKey="parent"
                    valueKey="url"
                    typeKey="type"
                    displayKey="text"
                    selectnodetype="file"
                    initialValue={selectedUrl ?? ""}
                    items={siteMedia}
                    onItemSelect={handleTreeSelect}
                    openText="select site image"
                    closeText="close image list"
                    style={{ flex: "1 1 0", minWidth: 0 }}
                    rootNodeKeyValue="#"
                    closeOnSelect={false}
                    hideClear={true} 
                    popoverTitle="Select Media"
                    sortBy={TreeItemSort.Ascending}
                    triggerClassName="page-selector__trigger"
                  />

                </InputGroup>
              )
              :
              (
                <div className="mt-2" hidden={media.items === undefined}>
                  <AppEmptyState
                    compact={true}
                    title="No site media found"
                    message="Upload media first or use an external URL for this item."
                  />
                  <div className="mt-2 text-center">
                    <Link to="/content/mediamanager">Open media manager</Link>
                  </div>
                </div>
              )
            }
          </Col>
          <Col md={12} xl={6} className="m-0 p-0 p-4 text-center">
            <div className="border h-100">
              {selectedUrl && (
                mediaIsVideo() ? (
                  <video
                    src={selectedUrl}
                    className="img-fluid mw-100 p-2"
                    style={{ maxHeight: "190px " }}
                    controls
                  ></video>
                ) : (
                  <img
                    src={selectedUrl}
                    className="img-fluid mw-100 p-2"
                    style={{ maxHeight: "190px " }}
                    alt="Media"
                  />
                )
              )}
              {!selectedUrl && (
                <div className="p-3 h-100 d-flex align-items-center">
                  <AppEmptyState
                    compact={true}
                    className="justify-content-center text-center"
                    title="No media selected"
                    message="Select an image or add a URL to an externally hosted one."
                  />
                </div>
              )}
            </div>
          </Col>
        </Row>
    </>
  );
}

export default SiteMediaPicker;
